import React from 'react';
import styled from 'libs/styled';
import { above, below } from '@fogia/core-web/utils/mediaqueries';
import { ArrowRightIcon } from 'assets/media/icons/ArrowRightIcon';
import { ChevronDown } from 'assets/media/icons/ChevronDown';
import Text from 'components/Text';

const filterTransientProps = { shouldForwardProp: (prop: string) => !prop.startsWith('$') };

const StickyBarWrapper = styled('div', filterTransientProps)<{ $isMinimized?: boolean }>`
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 99;
    display: flex;
    flex-direction: column;
    background-color: var(--ui-background);

    transition:
        background-color var(--transition-primary-medium),
        border-top var(--transition-primary-medium);

    ${above['tablet.lg']} {
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        height: 38px;
        background-color: ${({ $isMinimized }) => ($isMinimized ? 'transparent' : 'var(--ui-background)')};
        border-top: ${({ $isMinimized }) => ($isMinimized ? 'none' : '0.5px solid rgba(23, 23, 23, 0.15)')};
    }
`;

const StickyBarDropdownCollapse = styled('div', filterTransientProps)<{ $isExpanded: boolean }>`
    order: 2;
    display: grid;
    grid-template-rows: ${({ $isExpanded }) => ($isExpanded ? '1fr' : '0fr')};
    transition: grid-template-rows var(--transition-primary-medium);

    ${above['tablet.lg']} {
        display: contents;
    }
`;

const StickyBarDropdownCollapseInner = styled('div')`
    min-height: 0;
    overflow: hidden;

    ${above['tablet.lg']} {
        display: contents;
    }
`;

const DropdownButtonsContainer = styled('div', filterTransientProps)<{ $isHidden?: boolean }>`
    display: flex;
    flex: 1;

    ${below['tablet.lg']} {
        border-bottom: 1px solid rgba(23, 23, 23, 0.15);
    }

    ${above['tablet.lg']} {
        order: 1;
        transition:
            transform var(--transition-primary-medium),
            opacity var(--transition-primary-medium);
        transform: ${({ $isHidden }) => ($isHidden ? 'translateX(100%)' : 'translateX(0)')};
        opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
    }
`;

const DropdownButton = styled('button')`
    display: flex;
    align-items: center;
    gap: 10px;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0 10px;
    transition: opacity var(--transition-primary-fast);

    &:hover {
        opacity: 0.7;
    }

    ${below['tablet.lg']} {
        flex: 1;
        height: 36px;

        &:first-of-type {
            border-right: 1px solid rgba(23, 23, 23, 0.15);
        }
    }

    ${above['tablet.lg']} {
        height: 38px;
    }
`;

const DropdownText = styled(Text)`
    margin: 0;
    white-space: nowrap;
`;

const DropdownValueText = styled(Text)`
    margin: 0;
    white-space: nowrap;

    ${below['tablet.lg']} {
        display: none;
    }
`;

const CTAButton = styled('button')`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 10px;
    background-color: var(--ui-secondary);
    border: none;
    cursor: pointer;
    transition: opacity var(--transition-primary-fast);

    &:hover {
        opacity: 0.9;
    }

    ${below['tablet.lg']} {
        width: 100%;
        height: 38px;
        order: 1;
    }

    ${above['tablet.lg']} {
        width: 477px;
        height: 38px;
        order: 2;
        position: relative;
        z-index: 1;
    }
`;

const CTAText = styled(Text)`
    flex: 1;
    margin: 0;
    white-space: nowrap;
    text-align: left;
`;

export interface StickyBarProps {
    selectedLooksLabel?: string;
    selectedLooksValue?: string;
    configurationLabel?: string;
    configurationValue?: string;
    ctaLabel?: string;
    onSelectedLooksClick?: () => void;
    onConfigurationClick?: () => void;
    onCtaClick?: () => void;
    isGalleryInView?: boolean;
    selectedLooksCount?: number;
}

const StickyBar: React.FC<StickyBarProps> = ({
    selectedLooksLabel = 'Selected looks',
    selectedLooksValue = '',
    selectedLooksCount = 0,
    configurationLabel = 'Product family',
    configurationValue = '',
    ctaLabel = 'Get a quotation',
    onSelectedLooksClick,
    onConfigurationClick,
    onCtaClick,
    isGalleryInView = true,
}) => {
    const handleSelectedLooksClick = () => {
        onSelectedLooksClick?.();
    };

    const handleConfigurationClick = () => {
        onConfigurationClick?.();
    };

    const handleCtaClick = () => {
        onCtaClick?.();
    };

    return (
        <StickyBarWrapper $isMinimized={!isGalleryInView}>
            {/* CTA Button - appears first on mobile (top), last on desktop (right) */}
            <CTAButton onClick={handleCtaClick}>
                <CTAText
                    $as="span"
                    typography={['Primary/Mobile/13_100_510_-1', null, null, null, 'Primary/15_120_510_-1']}
                    color="var(--ui-primary)"
                >
                    {ctaLabel}
                </CTAText>
                <ArrowRightIcon color="var(--ui-primary)" size="xsmall" width="10px" height="10px" />
            </CTAButton>

            {/* Dropdown row: height collapse on narrow viewports; slide on wide */}
            <StickyBarDropdownCollapse $isExpanded={isGalleryInView}>
                <StickyBarDropdownCollapseInner>
                    <DropdownButtonsContainer $isHidden={!isGalleryInView}>
                        {selectedLooksCount > 0 && (
                            <DropdownButton onClick={handleSelectedLooksClick}>
                                <DropdownText
                                    $as="span"
                                    typography={[
                                        'Primary/Mobile/13_100_510_-1',
                                        null,
                                        null,
                                        null,
                                        'Primary/15_120_510_-1',
                                    ]}
                                    color="var(--ui-primary)"
                                >
                                    {selectedLooksLabel}
                                </DropdownText>
                                <DropdownValueText
                                    $as="span"
                                    typography={[
                                        'Secondary/Mobile/13_120_400',
                                        null,
                                        null,
                                        null,
                                        'Secondary/15_120_400',
                                    ]}
                                    color="var(--ui-primary)"
                                >
                                    {selectedLooksValue}
                                </DropdownValueText>
                                <ChevronDown color="var(--ui-primary)" size="xsmall" width="10px" height="10px" />
                            </DropdownButton>
                        )}
                        <DropdownButton onClick={handleConfigurationClick}>
                            <DropdownText
                                $as="span"
                                typography={['Primary/Mobile/13_100_510_-1', null, null, null, 'Primary/15_120_510_-1']}
                                color="var(--ui-primary)"
                            >
                                {configurationLabel}
                            </DropdownText>
                            <DropdownValueText
                                $as="span"
                                typography={['Secondary/Mobile/13_120_400', null, null, null, 'Secondary/15_120_400']}
                                color="var(--ui-primary)"
                            >
                                {configurationValue}
                            </DropdownValueText>
                            <ChevronDown color="var(--ui-primary)" size="xsmall" width="10px" height="10px" />
                        </DropdownButton>
                    </DropdownButtonsContainer>
                </StickyBarDropdownCollapseInner>
            </StickyBarDropdownCollapse>
        </StickyBarWrapper>
    );
};

export default StickyBar;
